"use client";

import React, { useState } from "react";
import { TriangleAlert as AlertTriangle, ArrowRight, CircleCheck as CheckCircle2, Clock, Droplet, Flame, Gauge, PhoneCall, ShieldAlert, Wrench, Zap } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const problems = [{
  id: "burst",
  title: "Burst or Ruptured Pipe",
  severity: "Critical Emergency",
  severityVariant: "destructive" as const,
  iconName: "Flame",
  signs: "Rapid pooling water, loud popping noise, immediate loss of water pressure",
  danger: "Structural ceiling collapse, mold proliferation, immediate electrical hazard",
  firstStep: "Shut off main water valve immediately and turn off electricity to wet zones.",
  dispatchTime: "15-30 Min Rapid Response"
}, {
  id: "leak",
  title: "Hidden & Pin-Hole Leaks",
  severity: "High Urgency",
  severityVariant: "default" as const,
  iconName: "Droplet",
  signs: "Unexplained high water bills, damp drywall, musty odors, warm spots on floors",
  danger: "Sub-floor rotting, foundation movement, hidden bacterial growth",
  firstStep: "Isolate nearby fixture shutoffs and avoid running appliances until inspected.",
  dispatchTime: "Same-Day Emergency Service"
}, {
  id: "corrosion",
  title: "Pipe Corrosion & Rust",
  severity: "Moderate Priority",
  severityVariant: "secondary" as const,
  iconName: "ShieldAlert",
  signs: "Discolored reddish or brownish water, metallic taste, visible flaking on exposed pipes",
  danger: "Pipe wall thinning leading to sudden catastrophic ruptures",
  firstStep: "Collect a water sample and refrain from drinking discolored tap water.",
  dispatchTime: "Scheduled or Priority Dispatch"
}, {
  id: "pressure",
  title: "Sudden Low Water Pressure",
  severity: "Moderate Priority",
  severityVariant: "outline" as const,
  iconName: "Gauge",
  signs: "Weak flow at multiple faucets, sputtering taps, hot water line trickling",
  danger: "Indicates hidden main line fracture, sediment blockage, or municipal backflow",
  firstStep: "Check if the issue affects one fixture or the entire home to locate the restriction.",
  dispatchTime: "Same-Day Diagnostics"
}];
const guidanceSteps = [{
  step: "1",
  title: "Identify the Symptoms",
  desc: "Check where water is collecting, whether pressure is down, or if water is discolored."
}, {
  step: "2",
  title: "Cut Main Supply",
  desc: "Turn your main water shutoff valve clockwise until fully closed to stop active flooding."
}, {
  step: "3",
  title: "Call Emergency Dispatch",
  desc: "Our licensed Jonesboro plumbers arrive with thermal leak detectors and full replacement parts."
}];
export default function PipeTriage() {
  const shouldReduceMotion = useReducedMotion();
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const filteredProblems = selectedCategory === "all" ? problems : problems.filter(p => p.id === selectedCategory);
  return <section id="pipeproblemstriage" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <div className="flex flex-col items-center text-center max-w-3xl mx-auto mb-12">
          <Badge variant="outline" className="mb-4 px-3 py-1 text-xs uppercase tracking-wider font-semibold border-border bg-secondary/50">
            Emergency Diagnostic Guide
          </Badge>
          <h2 className="font-default text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground">
            Identify & Triage Pipe Emergencies
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Plumbing pipe failures escalate quickly. Use our triage guide below to assess damage severity, take immediate safety steps, and dispatch a licensed Jonesboro technician.
          </p>
          
          {/* Quick Filter Tabs */}
          <div className="mt-8 flex flex-wrap justify-center gap-2">
            <Button variant={selectedCategory === "all" ? "default" : "outline"} size="sm" onClick={() => setSelectedCategory("all")} className="rounded-full px-4">
              All Pipe Issues
            </Button>
            <Button variant={selectedCategory === "burst" ? "default" : "outline"} size="sm" onClick={() => setSelectedCategory("burst")} className="rounded-full px-4">
              Burst Pipes
            </Button>
            <Button variant={selectedCategory === "leak" ? "default" : "outline"} size="sm" onClick={() => setSelectedCategory("leak")} className="rounded-full px-4">
              Hidden Leaks
            </Button>
            <Button variant={selectedCategory === "corrosion" ? "default" : "outline"} size="sm" onClick={() => setSelectedCategory("corrosion")} className="rounded-full px-4">
              Corrosion & Rust
            </Button>
            <Button variant={selectedCategory === "pressure" ? "default" : "outline"} size="sm" onClick={() => setSelectedCategory("pressure")} className="rounded-full px-4">
              Low Pressure
            </Button>
          </div>
        </div>

        {/* Problem Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {filteredProblems.map((item, index) => {
          return <motion.div key={item.id} data-index={index} initial={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 0,
            y: 16
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.35,
            delay: index * 0.08
          }} className="h-full">
                <Card className="h-full border border-border bg-card shadow-sm hover:shadow-md transition-shadow flex flex-col justify-between">
                  <CardHeader className="space-y-3 pb-4 border-b border-border/60">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2.5">
                        <div className="p-2 rounded-lg bg-primary/10 text-primary">
                          {item.id === "burst" && <Zap className="size-5" />}
                          {item.id === "leak" && <Droplet className="size-5" />}
                          {item.id === "corrosion" && <ShieldAlert className="size-5" />}
                          {item.id === "pressure" && <Gauge className="size-5" />}
                        </div>
                        <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                          Problem Profile #{index + 1}
                        </span>
                      </div>
                      <Badge variant={item.severityVariant} className="font-semibold">
                        {item.severity}
                      </Badge>
                    </div>
                    <CardTitle className="text-xl sm:text-2xl font-bold text-card-foreground">
                      {item.title}
                    </CardTitle>
                    <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
                      <Clock className="size-3.5 text-primary" />
                      <span>Est. Response: {item.dispatchTime}</span>
                    </div>
                  </CardHeader>

                  <CardContent className="space-y-4 pt-4 text-sm">
                    <div>
                      <div className="font-semibold text-foreground mb-1 flex items-center gap-1.5">
                        <AlertTriangle className="size-4 text-accent" />
                        Common Symptoms:
                      </div>
                      <p className="text-muted-foreground leading-normal">{item.signs}</p>
                    </div>

                    <div>
                      <div className="font-semibold text-foreground mb-1 flex items-center gap-1.5">
                        <ShieldAlert className="size-4 text-destructive" />
                        Risk If Delayed:
                      </div>
                      <p className="text-muted-foreground leading-normal">{item.danger}</p>
                    </div>

                    <div className="bg-muted/70 rounded-lg p-3.5 border border-border">
                      <div className="font-semibold text-foreground text-xs uppercase tracking-wider mb-1 flex items-center gap-1.5">
                        <CheckCircle2 className="size-4 text-primary" />
                        Immediate Action Step:
                      </div>
                      <p className="text-foreground text-xs leading-relaxed font-medium">
                        {item.firstStep}
                      </p>
                    </div>
                  </CardContent>

                  <CardFooter className="pt-2 border-t border-border/60 flex items-center justify-between gap-3">
                    <Button variant="outline" size="sm" asChild className="text-xs">
                      <Link to="/problems/" className="flex items-center gap-1">
                        View Repair Info
                        <ArrowRight className="size-3" />
                      </Link>
                    </Button>

                    <Button size="sm" asChild className="bg-primary text-primary-foreground hover:bg-primary/90 text-xs">
                      <Link to="/contact/" className="flex items-center gap-1.5">
                        <PhoneCall className="size-3.5" />
                        Request Plumber
                      </Link>
                    </Button>
                  </CardFooter>
                </Card>
              </motion.div>;
        })}
        </div>

        {/* 3-Step Protocol Bar */}
        <div className="mt-16 bg-card border border-border rounded-2xl p-6 sm:p-8 lg:p-10 shadow-xs">
          <div className="max-w-2xl mb-8">
            <span className="text-xs uppercase tracking-widest font-bold text-primary">Standard Operating Procedure</span>
            <h3 className="text-2xl font-bold text-foreground mt-1">
              Immediate 3-Step Protocol for Pipe Emergencies
            </h3>
            <p className="text-muted-foreground text-sm mt-2">
              Follow these three immediate measures to prevent structural water damage in your home before our dispatch team arrives.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {guidanceSteps.map((stepItem, stepIdx) => <div key={stepItem.step} data-index={stepIdx} className="flex flex-col bg-background border border-border/80 rounded-xl p-5 shadow-2xs relative">
                <div className="flex items-center justify-between mb-3">
                  <div className="size-8 rounded-full bg-primary text-primary-foreground font-bold flex items-center justify-center text-sm">
                    {stepItem.step}
                  </div>
                  <Wrench className="size-4 text-muted-foreground" />
                </div>
                <h4 className="font-semibold text-foreground text-base mb-1.5">{stepItem.title}</h4>
                <p className="text-muted-foreground text-sm leading-relaxed">{stepItem.desc}</p>
              </div>)}
          </div>

          {/* Emergency Contact Banner inside Container */}
          <div className="mt-8 pt-6 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3 text-center sm:text-left">
              <div className="p-3 bg-destructive/10 text-destructive rounded-full shrink-0">
                <Flame className="size-6" />
              </div>
              <div>
                <p className="font-bold text-foreground text-base">Active pipe burst or flooding right now?</p>
                <p className="text-muted-foreground text-xs sm:text-sm">24/7 on-call trucks standing by across Jonesboro, GA.</p>
              </div>
            </div>
            <Button size="lg" asChild className="w-full sm:w-auto bg-primary text-primary-foreground hover:bg-primary/90 font-semibold px-6">
              <Link to="/contact/" className="flex items-center gap-2">
                <PhoneCall className="size-4" />
                Dispatch Plumber Now
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}